/**
 * PURPOSE: Cliente de opciones de uniforme normalizadas (FTR-0014).
 * RESPONSIBILITIES: Estado de los flags; administración por compañía (eventos, deportes, matriz, CRUD y
 *   orden de hasta 3 opciones por tipo y tono, foto por opción, plazos por evento) con la cabecera
 *   X-Hayllis-Company del interceptor de FTR-0012; y la asignación por partido desde Programación (contexto,
 *   asignar por ID, masiva, inteligente), el historial de uniformes de un partido (FTR-0044) y las versiones de
 *   uniformes del torneo como espacios de trabajo (FTR-0046). Devuelve el sobre V2 desenvuelto y propaga los
 *   errores HTTP al llamador.
 * COLLABORATORS: httpService.js, CompanyUniformsTab.jsx, MatchUniformOptionsFields.jsx, ProgramacionArea.jsx,
 *   useUniformOptionsStatus.js.
 * ROUTE: winscore/src/services/uniformOptionsService.js
 */
import httpService from "./httpService.js";

export const UNIFORM_OPTIONS_ADMIN_BASE = "/api/hayllis/v2/admin/uniform-options";
export const UNIFORM_ASSIGNMENTS_BASE = "/api/hayllis/v2/uniform-assignments";
export const UNIFORM_OPTIONS_STATUS_PATH = "/api/hayllis/v2/uniform-options/status";

const unwrap = (response) => response?.data?.data ?? {};
const id = (value) => encodeURIComponent(String(value));
const teamPath = (idparticipant, suffix = "") => `${UNIFORM_OPTIONS_ADMIN_BASE}/teams/${id(idparticipant)}${suffix}`;

/** Flags del backend; ante cualquier error se asume apagado (los editores legacy siguen protegidos en el servidor). */
export const getUniformOptionsStatus = async () => {
  try {
    const data = unwrap(await httpService.get(UNIFORM_OPTIONS_STATUS_PATH));
    return { enabled: data.enabled === true, publicEnabled: data.publicEnabled === true };
  } catch (error) {
    return { enabled: false, publicEnabled: false };
  }
};

// ----------------------------- Administración General → Uniformes -----------------------------
export const listUniformEvents = async () => {
  const { events } = unwrap(await httpService.get(`${UNIFORM_OPTIONS_ADMIN_BASE}/events`));
  return Array.isArray(events) ? events : [];
};

export const listUniformEventSports = async (idevent) => {
  const { sports } = unwrap(await httpService.get(`${UNIFORM_OPTIONS_ADMIN_BASE}/events/${id(idevent)}/sports`));
  return Array.isArray(sports) ? sports : [];
};

export const getUniformMatrix = async ({ idevent, idsport }) =>
  unwrap(await httpService.get(`${UNIFORM_OPTIONS_ADMIN_BASE}/matrix`, { params: { idevent, idsport } }));

export const createUniformOption = async (idparticipant, payload) =>
  unwrap(await httpService.post(teamPath(idparticipant, "/options"), payload));

export const updateUniformOption = async (idparticipant, idoption, payload) =>
  unwrap(await httpService.patch(teamPath(idparticipant, `/options/${id(idoption)}`), payload));

export const deleteUniformOption = async (idparticipant, idoption) =>
  unwrap(await httpService.delete(teamPath(idparticipant, `/options/${id(idoption)}`)));

export const reorderUniformOptions = async (idparticipant, { idtype, tone, optionIds }) =>
  unwrap(await httpService.put(teamPath(idparticipant, "/order"), { idtype, tone, optionIds }));

export const uploadUniformOptionPhoto = async (idparticipant, idoption, file) => {
  const formData = new FormData();
  formData.append("photo", file);
  return unwrap(
    await httpService.post(teamPath(idparticipant, `/options/${id(idoption)}/photo`), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  );
};

export const removeUniformOptionPhoto = async (idparticipant, idoption) =>
  unwrap(await httpService.delete(teamPath(idparticipant, `/options/${id(idoption)}/photo`)));

export const getUniformPolicies = async (idevent) =>
  unwrap(await httpService.get(`${UNIFORM_OPTIONS_ADMIN_BASE}/events/${id(idevent)}/policies`));

export const saveUniformPolicy = async (idevent, payload) =>
  unwrap(await httpService.put(`${UNIFORM_OPTIONS_ADMIN_BASE}/events/${id(idevent)}/policies`, payload));

export const disableUniformPolicy = async (idevent, idpolicy, reason = "") => {
  const trimmed = String(reason || "").trim();
  return unwrap(
    await httpService.delete(`${UNIFORM_OPTIONS_ADMIN_BASE}/events/${id(idevent)}/policies/${id(idpolicy)}`, {
      data: trimmed ? { reason: trimmed } : {},
    })
  );
};

// ----------------------------------------- Programación -----------------------------------------
export const getMatchUniformContext = async (idmatch) =>
  unwrap(await httpService.get(`${UNIFORM_ASSIGNMENTS_BASE}/matches/${id(idmatch)}`));

export const assignMatchUniforms = async (idmatch, assignments) =>
  unwrap(await httpService.put(`${UNIFORM_ASSIGNMENTS_BASE}/matches/${id(idmatch)}`, { assignments }));

export const bulkAssignMatchUniforms = async ({ idevent, idsport, matches, local }) =>
  unwrap(await httpService.post(`${UNIFORM_ASSIGNMENTS_BASE}/bulk`, { idevent, idsport, matches, local }));

// FTR-0041: asignación inteligente. La vista previa nunca escribe; la aplicación envía el planHash de esa vista.
export const previewAutoUniforms = async (body) =>
  unwrap(await httpService.post(`${UNIFORM_ASSIGNMENTS_BASE}/auto/preview`, body));

export const applyAutoUniforms = async (body) =>
  unwrap(await httpService.post(`${UNIFORM_ASSIGNMENTS_BASE}/auto/apply`, body));

// FTR-0044: historial de uniformes de un partido.
export const getMatchUniformHistory = async (idmatch) =>
  unwrap(await httpService.get(`${UNIFORM_ASSIGNMENTS_BASE}/matches/${id(idmatch)}/history`));

// FTR-0046: versiones de uniformes del torneo (evento + deporte) como espacios de trabajo.
const versionList = (data) => ({
  available: data.available !== false,
  enabled: data.enabled === true,
  eligible: data.eligible === true,
  exclusiveStrategy: data.exclusiveStrategy || null,
  active: data.active || null,
  versions: Array.isArray(data.versions) ? data.versions : [],
});

/** Estado del versionamiento y versiones; con el versionamiento activo el backend garantiza la V1. */
export const listUniformVersions = async ({ idevent, idsport }) =>
  versionList(unwrap(await httpService.get(`${UNIFORM_ASSIGNMENTS_BASE}/versions`, { params: { idevent, idsport } })));

/** Nueva versión: copia de la que está en uso, que pasa a ser la nueva en uso. Devuelve la lista y `created`. */
export const createUniformVersion = async ({ idevent, idsport, name }) => {
  const body = { idevent: Number(idevent), idsport: Number(idsport) };
  if (String(name || "").trim()) body.name = String(name).trim();
  const data = unwrap(await httpService.post(`${UNIFORM_ASSIGNMENTS_BASE}/versions`, body));
  return { ...versionList(data), created: data.created || null };
};

/** Usar una versión: guarda la actual y los partidos pasan a mostrar la elegida. */
export const activateUniformVersion = async (idversion) =>
  unwrap(await httpService.post(`${UNIFORM_ASSIGNMENTS_BASE}/versions/${id(idversion)}/activate`, {}));

/** Elimina una versión que no está en uso (nunca V1). Devuelve la lista. */
export const deleteUniformVersion = async (idversion) =>
  versionList(unwrap(await httpService.delete(`${UNIFORM_ASSIGNMENTS_BASE}/versions/${id(idversion)}`)));
